
import React from 'react';

export const ExchangeStrategies: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            1031 Exchange Strategies for Apartment Buildings
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            1031 exchanges offer powerful tax deferral opportunities for apartment building investors. Understanding 
            the rules, strategies, and alternatives helps you maximize wealth preservation and growth.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">1031 Exchange Requirements</h3>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Like-Kind Property</h4>
                  <p className="text-gray-700 mb-3">
                    Investment or business-use real estate can be exchanged for other investment or business-use 
                    real estate. Apartment buildings can be exchanged for other apartments, office buildings, 
                    retail centers, or other commercial properties.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="text-sm text-blue-800 font-medium">Key Point:</div>
                    <div className="text-gray-600 text-xs">Personal residences don't qualify for 1031 exchanges</div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Equal or Greater Value</h4>
                  <p className="text-gray-700 mb-3">
                    The replacement property must be of equal or greater value than the relinquished property. 
                    Any "boot" (cash received) is taxable in the year of the exchange.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <div className="text-sm">
                      <div className="text-green-800 font-medium">Example:</div>
                      <div className="text-gray-600 text-xs">Sell $2M property → Buy $2.2M property = Full deferral</div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-yellow-800 mb-3">Qualified Intermediary</h4>
                  <p className="text-gray-700 mb-3">
                    Must use a qualified intermediary (QI) to facilitate the exchange. The QI holds the proceeds 
                    from the sale until they're used to purchase the replacement property.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-yellow-200">
                    <div className="text-sm">
                      <div className="text-yellow-800 font-medium">Critical:</div>
                      <div className="text-gray-600 text-xs">Cannot take possession of sale proceeds</div>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-800 mb-3">Strict Timeline</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Identify Replacement Property:</span>
                      <span className="bg-red-200 text-red-800 px-3 py-1 rounded-full text-xs font-medium">45 Days</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Complete Exchange:</span>
                      <span className="bg-red-200 text-red-800 px-3 py-1 rounded-full text-xs font-medium">180 Days</span>
                    </div>
                    <p className="text-red-700 text-xs">
                      Deadlines are absolute - no extensions allowed
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/31d0f0411fdacb973b269ce9c7f73eef.jpg"
              alt="1031 exchange timeline and process for apartment building transactions"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Exchange Strategy Options</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-swap-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Simultaneous Exchange</h4>
              <p className="text-gray-600 text-sm mb-4">
                Both properties close on the same day. Most challenging to coordinate but provides certainty 
                that both transactions will complete successfully.
              </p>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-xs text-blue-800 font-medium">Pros:</div>
                <div className="text-xs text-gray-600">Maximum certainty, no timing risk</div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Delayed Exchange</h4>
              <p className="text-gray-600 text-sm mb-4">
                Most common type. Sell first, then identify and purchase replacement property within the 
                required timelines using a qualified intermediary.
              </p>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-xs text-green-800 font-medium">Pros:</div>
                <div className="text-xs text-gray-600">Flexibility, easier coordination</div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-2-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Build-to-Suit</h4>
              <p className="text-gray-600 text-sm mb-4">
                Purchase land and construct or significantly improve a property as your replacement property. 
                Improvements must be completed within the 180-day timeline.
              </p>
              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="text-xs text-purple-800 font-medium">Best For:</div>
                <div className="text-xs text-gray-600">Custom requirements, limited inventory</div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-arrow-up-down-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Reverse Exchange</h4>
              <p className="text-gray-600 text-sm mb-4">
                Purchase the replacement property before selling the relinquished property. Requires parking 
                the replacement property with an exchange accommodation titleholder.
              </p>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <div className="text-xs text-yellow-800 font-medium">Use Case:</div>
                <div className="text-xs text-gray-600">Competitive markets, perfect properties</div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-team-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Tenant-in-Common</h4>
              <p className="text-gray-600 text-sm mb-4">
                Purchase fractional interests in larger properties through TIC arrangements. Allows access 
                to institutional-quality properties with smaller capital requirements.
              </p>
              <div className="bg-teal-50 p-3 rounded-lg">
                <div className="text-xs text-teal-800 font-medium">Benefits:</div>
                <div className="text-xs text-gray-600">Diversification, professional management</div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-funds-box-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Delaware Statutory Trust</h4>
              <p className="text-gray-600 text-sm mb-4">
                Purchase interests in professionally managed real estate through DST structures. Provides 
                passive ownership with institutional-quality properties and management.
              </p>
              <div className="bg-red-50 p-3 rounded-lg">
                <div className="text-xs text-red-800 font-medium">Ideal For:</div>
                <div className="text-xs text-gray-600">Passive investors, estate planning</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=tax%20savings%20visualization%20for%201031%20exchange%20benefits%2C%20capital%20gains%20tax%20deferral%20comparison%20chart%2C%20apartment%20building%20investment%20wealth%20preservation%20strategy%20with%20compound%20growth%20benefits&width=600&height=400&seq=tax-savings-benefits&orientation=landscape"
              alt="1031 exchange tax savings and wealth preservation benefits"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Financial Benefits Analysis</h3>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Tax Deferral Impact</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Property Sale Price:</span>
                      <span className="font-medium">$2,000,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Original Basis:</span>
                      <span className="font-medium">$1,200,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Capital Gain:</span>
                      <span className="font-medium">$800,000</span>
                    </div>
                    <div className="flex justify-between border-t border-green-200 pt-2">
                      <span className="text-gray-700">Tax Without Exchange (25%):</span>
                      <span className="font-bold text-red-600">$200,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Tax With 1031 Exchange:</span>
                      <span className="font-bold text-green-600">$0</span>
                    </div>
                    <div className="flex justify-between bg-green-100 p-3 rounded-lg font-bold">
                      <span className="text-green-800">Additional Capital to Invest:</span>
                      <span className="text-green-800">$200,000</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Compound Growth Advantage</h4>
                  <p className="text-gray-700 text-sm mb-3">
                    The $200,000 tax savings reinvested at 7% annual returns grows to over $800,000 in 20 years. 
                    This demonstrates the power of tax deferral combined with compound growth.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-800">4x Growth</div>
                      <div className="text-xs text-gray-600">Tax savings after 20 years</div>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-purple-800 mb-2">Estate Planning Benefits</h5>
                  <p className="text-purple-700 text-sm">
                    Properties held until death receive a "stepped-up basis," potentially eliminating 
                    capital gains taxes entirely for your heirs while preserving wealth through generations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">1031 Exchanges: The Ultimate Wealth Building Tool</h3>
            <p className="text-lg opacity-90 leading-relaxed mb-4">
              1031 exchanges allow you to build wealth faster by keeping more capital working for you instead of 
              paying it to the government in taxes. The ability to defer taxes indefinitely while upgrading properties 
              creates a powerful wealth accumulation strategy.
            </p>
            <div className="text-sm opacity-75">
              <strong>Remember:</strong> 1031 exchanges have strict rules and deadlines. Work with experienced 
              qualified intermediaries and tax professionals to ensure compliance and maximize benefits.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
